/**
 * FogBugz toolset constants (epic PI-SPEC-SW-E307, ER-1663, contract
 * fogbugz 1).
 *
 * Single source of truth for the EVTX summary on an existing FogBugz case:
 * the four section headings, the character caps, the marker line name, the
 * turn timeout and the provenance line. The SpecTree API (digest and
 * reviewed-post routes), the SpecTree web review step, the internal plugin
 * skill and the SpecTree Toolbox fogbugz engine all import these; never
 * re-type them. Browser-safe: no runtime dependencies (the zod schemas that
 * use these live in `schemas.ts`).
 */
/**
 * The four level-2 headings the summary sections carry, in this order. The
 * same headings, in the same order, as the website's server-side summary
 * (`EVTX_SUMMARY_SYSTEM_PROMPT` in the SpecTree API's
 * `services/evtxSummaryService.ts`), so both summaries read alike.
 */
export const FOGBUGZ_EVTX_SECTIONS = [
    "## Overview",
    "## Notable errors and warnings",
    "## Timeline",
    "## Suggested next steps",
];
/** Longest summary sections text (the four sections together) Claude may return or a person may post. */
export const FOGBUGZ_EVTX_SECTIONS_MAX_CHARS = 12_000;
/**
 * Longest digest markdown the summarize input and the digest response may
 * carry. The deterministic renderer (`@spectree/evtx` `renderEvtxDigestMarkdown`)
 * keeps its output within `DEFAULT_DIGEST_CHAR_BUDGET` (40,000 characters, room
 * for the "Full error messages" section); this cap sits above that bound so a
 * digest rendered at the default budget always fits.
 */
export const FOGBUGZ_EVTX_DIGEST_MAX_CHARS = 48_000;
/**
 * The marker the internal skill prints at the start of its one result line:
 * `SPECTREE_FOGBUGZ_EVTX {json}`. The JSON parses with
 * `fogbugzEvtxTurnMarkerSchema`.
 */
export const FOGBUGZ_EVTX_TURN_MARKER = "SPECTREE_FOGBUGZ_EVTX";
/** How long the engine waits for one EVTX summary turn before it stops it with `TIMED_OUT`. */
export const FOGBUGZ_EVTX_TURN_TIMEOUT_MS = 5 * 60_000;
/**
 * The provenance line of a Toolbox-written summary: the person's own Claude
 * wrote the sections from the parsed digest. Replaces the server's
 * "AI-generated from a parsed digest; verify against the attached .evtx."
 * for this path.
 */
export function FOGBUGZ_EVTX_PROVENANCE(personName) {
    return `Written by ${personName}'s Claude from the parsed digest; verify against the attached .evtx.`;
}
// ---------------------------------------------------------------------------
// "Look into the code" (epic PI-SPEC-SW-E307, feature PI-SPEC-SW-1602)
// ---------------------------------------------------------------------------
/**
 * The three level-2 headings of a "Look into the code" report, in this order:
 *
 * 1. `## Where it failed`: the area of the code the fault seems to come from;
 * 2. `## Code review`: what a code review would cover: who calls it, which
 *    exceptions it can throw and what each one means happened;
 * 3. `## Change history`: when the fault area and its callers last changed,
 *    so the report can say whether this looks like a new bug (a recent
 *    change) or an old bug that just happened to hit.
 */
export const FOGBUGZ_CODE_REPORT_SECTIONS = ["## Where it failed", "## Code review", "## Change history"];
/** Pass 1 marker: `SPECTREE_FOGBUGZ_CODE_LOCATE {json}`, parsed with `fogbugzCodeLocateMarkerSchema`. */
export const FOGBUGZ_CODE_LOCATE_MARKER = "SPECTREE_FOGBUGZ_CODE_LOCATE";
/** Pass 2 marker: `SPECTREE_FOGBUGZ_CODE_REPORT {json}`, parsed with `fogbugzCodeReportMarkerSchema`. */
export const FOGBUGZ_CODE_REPORT_MARKER = "SPECTREE_FOGBUGZ_CODE_REPORT";
/** Follow-up marker: `SPECTREE_FOGBUGZ_CODE_ANSWER {json}`, parsed with `fogbugzCodeAnswerMarkerSchema`. */
export const FOGBUGZ_CODE_ANSWER_MARKER = "SPECTREE_FOGBUGZ_CODE_ANSWER";
/** Version of the `turn.json` and `history.json` files the engine writes for the code skill. */
export const FOGBUGZ_CODE_TURN_INPUT_SCHEMA_VERSION = 1;
/** The three modes of the `toolbox-fogbugz-code` skill, one per Claude run. */
export const FOGBUGZ_CODE_TURN_MODES = ["locate", "report", "ask"];
/** Most follow-up questions one thread takes; the next one resolves with `TOO_MANY_FOLLOW_UPS`. */
export const FOGBUGZ_CODE_MAX_FOLLOW_UPS = 10;
/**
 * How long `investigateCode` may run (both Claude passes and the git history
 * between them) before the engine stops it with `TIMED_OUT`.
 */
export const FOGBUGZ_CODE_REPORT_TIMEOUT_MS = 10 * 60_000;
/** How long one follow-up (`askCode`) may run before the engine stops it with `TIMED_OUT`. */
export const FOGBUGZ_CODE_ASK_TIMEOUT_MS = 5 * 60_000;
/**
 * How long a thread lives without a follow-up. After that, and whenever a new
 * `investigateCode` starts (one thread per app), `askCode` for the old thread
 * resolves with `THREAD_EXPIRED` and the engine deletes its folder.
 */
export const FOGBUGZ_CODE_THREAD_IDLE_MS = 60 * 60_000;
/** Longest report (the three sections together) Claude may return. */
export const FOGBUGZ_CODE_REPORT_MAX_CHARS = 20_000;
/** Longest follow-up answer Claude may return. */
export const FOGBUGZ_CODE_ANSWER_MAX_CHARS = 8_000;
/** Longest follow-up question a person may type. */
export const FOGBUGZ_CODE_QUESTION_MAX_CHARS = 2_000;
/** Most code locations (the fault and its callers) one locate pass may name. */
export const FOGBUGZ_CODE_LOCATIONS_MAX = 20;
/** Most commits the engine writes into `history.json` for each located path, newest first. */
export const FOGBUGZ_CODE_HISTORY_MAX_COMMITS = 20;
/** A change at most this many days old is "recent" in the report's change history. */
export const FOGBUGZ_CODE_RECENT_DAYS = 30;
/** Most refs `listCodeRefs` returns (branches and tags together). */
export const FOGBUGZ_CODE_REFS_MAX = 200;
/** Longest branch or tag name. */
export const FOGBUGZ_CODE_REF_NAME_MAX = 200;
/** Longest plain reason a locate pass gives when it could not find the code. */
export const FOGBUGZ_CODE_NOT_FOUND_REASON_MAX_CHARS = 1_000;
/**
 * The phases a code run reports in `code-progress` events. Pages show
 * {@link FOGBUGZ_CODE_RUN_PHASE_LABELS}, never the id.
 */
export const FOGBUGZ_CODE_RUN_PHASES = ["finding-code", "reading-history", "writing-report", "answering"];
/** The plain phrase the page shows for each phase. */
export const FOGBUGZ_CODE_RUN_PHASE_LABELS = {
    "finding-code": "Finding the code",
    "reading-history": "Reading the change history",
    "writing-report": "Writing the report",
    answering: "Answering",
};
//# sourceMappingURL=constants.js.map